// PillarDial — single-pillar 270° radial dial used inside the
// PerformanceGrade composite. Colour is driven by the per-contract
// success / watch threshold bands so a critical pillar reads red on
// its own dial AND fuels the grade-cap floor rule.
//
// Track is muted slate; the filled arc starts at the bottom-left
// (225°) and sweeps clockwise to the bottom-right (-45°) — a 270°
// arc that leaves a 90° gap at the bottom. Score 100 fills the full
// arc; score 0 fills nothing.
//
// In-arc text is a two-line stack: percentage on top, sample
// annotation (n=N) below. The sample annotation always renders so
// the reader has an at-a-glance trust signal. When sample < minSample
// the fill arc is suppressed, the percentage becomes an em-dash, and
// the sub-label slot below the pillar name reads "awaiting data" so
// the row stays visually even with active dials.
//
// `minSample` is per-instance: deliverables pass 1 (one overdue item
// is unambiguous signal), milestones/KPIs pass 2 (a single point is
// noisy). The contract grade-gate uses MIN_SAMPLE=2 universally —
// dial activation is intentionally more permissive than the grade
// letter.

export type PillarThresholdBand = "success" | "watch" | "critical";

interface PillarDialProps {
  /** Pillar label (Deliverables / Milestones / KPIs). */
  label: string;
  /** Sub-label rendered under the pillar name. Locked vocabulary:
   *  "on time" (Deliverables, Milestones) / "on track" (KPIs).
   *  When the dial is awaiting data the slot displays
   *  "awaiting data" instead. */
  subLabel: string;
  /** Score 0–100. Null when the underlying data is unavailable. */
  score: number | null;
  /** Sample count. Compared against `minSample` to decide whether the
   *  dial renders as active or awaiting-data. */
  sampleSize: number;
  /** Per-pillar minimum sample size for the dial to activate.
   *  Deliverables = 1 (one overdue item is unambiguous signal even at
   *  n=1). Milestones / KPIs = 2 (a single point is noisy). The
   *  contract grade-gate uses MIN_SAMPLE=2 universally — dial
   *  activation is intentionally more permissive. */
  minSample: number;
  /** Per-contract threshold for the success band (default 90). */
  successThreshold: number;
  /** Per-contract threshold for the watch band (default 70). Below
   *  this value the pillar reads as "critical" and the dial is red. */
  watchThreshold: number;
  /** Stable id used for testid hooks (e.g. "deliverables"). */
  testId: string;
}

// Geometry: viewBox 100x100, centre (50,50), radius 40, stroke 8.
// 270° arc starts at angle 135° (bottom-left) and sweeps clockwise to
// angle -45° (bottom-right). SVG arc coordinates use:
//   x = cx + r * cos(angleRad)
//   y = cy - r * sin(angleRad)  // y-flip for screen space
const CX = 50;
const CY = 50;
const R = 40;
const STROKE = 8;

function polar(angleDeg: number): { x: number; y: number } {
  const rad = (angleDeg * Math.PI) / 180;
  return {
    x: CX + R * Math.cos(rad),
    y: CY - R * Math.sin(rad),
  };
}

const START_ANGLE = 225; // bottom-left
const END_ANGLE = -45; // bottom-right (clockwise from START)
const START = polar(START_ANGLE);
const END = polar(END_ANGLE);
// largeArcFlag = 1 because the sweep is > 180°. sweepFlag = 0 for
// clockwise direction (negative-y-up SVG convention).
const TRACK_PATH = `M ${START.x.toFixed(2)} ${START.y.toFixed(2)} A ${R} ${R} 0 1 1 ${END.x.toFixed(2)} ${END.y.toFixed(2)}`;

function thresholdBand(
  score: number,
  successThreshold: number,
  watchThreshold: number,
): PillarThresholdBand {
  if (score >= successThreshold) return "success";
  if (score >= watchThreshold) return "watch";
  return "critical";
}

const BAND_STROKE: Record<PillarThresholdBand, string> = {
  success: "stroke-emerald-500 dark:stroke-emerald-400",
  watch: "stroke-amber-500 dark:stroke-amber-400",
  critical: "stroke-red-600 dark:stroke-red-400",
};

const BAND_TEXT: Record<PillarThresholdBand, string> = {
  success: "text-emerald-700 dark:text-emerald-300",
  watch: "text-amber-700 dark:text-amber-300",
  critical: "text-red-700 dark:text-red-300",
};

export function PillarDial({
  label,
  subLabel,
  score,
  sampleSize,
  minSample,
  successThreshold,
  watchThreshold,
  testId,
}: PillarDialProps) {
  const awaiting = sampleSize < minSample || score === null;
  const band: PillarThresholdBand | null = awaiting
    ? null
    : thresholdBand(score, successThreshold, watchThreshold);

  // Arc length is computed from the path length the dasharray engine
  // sees. Using pathLength="100" lets us specify the filled portion
  // as a percentage directly without measuring arc length on mount.
  const fillPct = awaiting ? 0 : Math.max(0, Math.min(100, score));

  const valueClass = awaiting
    ? "text-slate-500 dark:text-slate-400"
    : (BAND_TEXT[band as PillarThresholdBand] ?? "");

  const ariaLabel = awaiting
    ? `${label}: awaiting data (sample size ${sampleSize})`
    : `${label}: ${Math.round(score)}% — ${band}`;

  return (
    <div
      data-testid={`pillar-dial-${testId}`}
      data-band={band ?? "awaiting"}
      className="flex flex-col items-center"
      aria-label={ariaLabel}
    >
      <svg
        viewBox="0 0 100 100"
        className="h-28 w-28"
        aria-hidden="true"
        role="img"
      >
        <path
          d={TRACK_PATH}
          fill="none"
          strokeWidth={STROKE}
          strokeLinecap="round"
          className="stroke-slate-200 dark:stroke-slate-700"
        />
        {!awaiting && (
          <path
            data-testid={`pillar-dial-${testId}-fill`}
            d={TRACK_PATH}
            fill="none"
            strokeWidth={STROKE}
            strokeLinecap="round"
            pathLength={100}
            strokeDasharray={`${fillPct} 100`}
            className={BAND_STROKE[band as PillarThresholdBand]}
          />
        )}
        <text
          x="50"
          y="50"
          textAnchor="middle"
          className={`fill-current text-h4 font-semibold ${valueClass}`}
          data-testid={`pillar-dial-${testId}-value`}
        >
          {awaiting ? "—" : `${Math.round(score)}%`}
        </text>
        <text
          x="50"
          y="64"
          textAnchor="middle"
          className="fill-current text-[9px] font-medium text-slate-500 dark:fill-slate-400 dark:text-slate-400"
          data-testid={`pillar-dial-${testId}-sample`}
        >
          n={sampleSize}
        </text>
      </svg>
      <div className="mt-2 text-body-sm font-medium text-slate-700 dark:text-slate-200">
        {label}
      </div>
      <div
        className="text-caption text-slate-500 dark:text-slate-400"
        data-testid={`pillar-dial-${testId}-sublabel`}
      >
        {awaiting ? "awaiting data" : subLabel}
      </div>
    </div>
  );
}

export default PillarDial;
